// @flow
/** @jsx jsx */

import { marginToCss } from '@zapier/common-utils';
import { type PropsQueriesType, PropsQuery } from '@zapier/common-components';
import { css, jsx } from '@emotion/core';

import type { Node } from 'react';
import type { Spacing } from '@zapier/common-utils';

type Props = {
  /** Flex alignItems value */
  alignItems: 'between' | 'center' | 'flex-end' | 'flex-start',
  children: Node,
  /** Flex direction */
  direction: 'column' | 'row',
  /** Join buttons with no margin */
  isConjoined: boolean,
  /** Apply flex to buttons */
  isFlex: boolean,
  /** Reverse flex direction */
  isReversed: boolean,
  /** Flex justify-content value */
  justify: 'between' | 'center' | 'flex-end' | 'flex-start',
  /** Custom margin (spacing utility) */
  margin?: Spacing,
  /** Props Queries */
  propsQueries?: PropsQueriesType,
};

const buttonGroupCss = ({
  alignItems,
  direction,
  isConjoined,
  isFlex,
  isReversed,
  justify,
  margin,
}: Props) =>
  css(
    {
      alignItems,
      display: 'flex',
      flexDirection: `${direction}${isReversed ? '-reverse' : ''}`,
      justifyContent: justify,
      '> *': {
        display: 'block',
        flex: isFlex ? 1 : 'none',
        width: direction === 'row' ? 'auto' : '100%',
      },
      ...marginToCss(margin),
    },
    isConjoined
      ? {
          '> *:not(:first-of-type)': {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            borderLeftWidth: 0,
          },
          '> *:not(:last-of-type)': {
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
          },
        }
      : {
          [`> *:not(:${isReversed ? 'first' : 'last'}-of-type)`]: {
            margin: direction === 'column' ? '0 0 1em' : '0 1em 0 0',
          },
        }
  );

const ButtonGroup = (props: Props) => {
  return (
    <PropsQuery queries={props.propsQueries}>
      {overrides => (
        <div css={buttonGroupCss({ ...props, ...overrides })}>
          {props.children}
        </div>
      )}
    </PropsQuery>
  );
};

ButtonGroup.defaultProps = {
  direction: 'column',
  isConjoined: false,
  isFlex: false,
  isReversed: false,
  justify: 'flex-start',
  alignItems: 'flex-start',
};

export default ButtonGroup;
